import React, { useEffect, useState } from 'react';
import { apiService } from '@/services/api';
import {
  MODALITY_ASYNC,
  MODALITY_VIDEO,
  setChosenVisitModality,
} from '@/lib/visitModality';

// §video-visit — the online / video choice, rendered by SquarePaymentForm
// above the card when the checkout passes a `modalityVisitType`. Renders
// nothing until GET /intake/visit-modality answers 'optional' or 'required'
// (see lib/visitModality.js).
const OPTIONS = [
  {
    value: MODALITY_ASYNC,
    title: 'Online visit',
    body: 'Your doctor reviews your answers and messages you.',
  },
  {
    value: MODALITY_VIDEO,
    title: 'Video visit',
    body: "Meet your doctor by video. You'll pick a time after checkout.",
  },
];

const VisitModalityChoice = ({ visitType, state, flow }) => {
  const [config, setConfig] = useState(null);
  const [value, setValue] = useState(MODALITY_ASYNC);

  useEffect(() => {
    let alive = true;
    setConfig(null);
    setChosenVisitModality(null);
    apiService.getVisitModality({ visitType, state, flow }).then((cfg) => {
      if (!alive) return;
      setConfig(cfg);
      if (cfg.modality === 'required') {
        setValue(MODALITY_VIDEO);
        setChosenVisitModality(MODALITY_VIDEO);
      } else if (cfg.modality === 'optional') {
        setValue(MODALITY_ASYNC);
        setChosenVisitModality(MODALITY_ASYNC);
      }
    });
    return () => {
      alive = false;
    };
  }, [visitType, state, flow]);

  if (!config || (config.modality !== 'optional' && config.modality !== 'required')) return null;
  const locked = config.modality === 'required';

  const pick = (v) => {
    if (locked) return;
    setValue(v);
    setChosenVisitModality(v);
  };

  return (
    <div className="mb-4" data-testid="visit-modality">
      <p className="text-sm font-semibold text-gray-900 mb-2">How would you like to see your doctor?</p>
      <div className="space-y-2" role="radiogroup" aria-label="Visit type">
        {OPTIONS.map((o) => {
          const selected = value === o.value;
          const disabled = locked && o.value !== MODALITY_VIDEO;
          if (disabled) return null;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-disabled={locked}
              data-testid={`visit-modality-${o.value}`}
              onClick={() => pick(o.value)}
              className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-colors ${
                selected ? 'border-[#2d8a5f] bg-[#f0faf4]' : 'border-gray-200 hover:border-[#2d8a5f]/60'
              } ${locked ? 'cursor-default' : ''}`}
            >
              <span className="block font-medium text-gray-900">{o.title}</span>
              <span className="block text-sm text-gray-500">{o.body}</span>
            </button>
          );
        })}
      </div>
      {locked && (
        <p className="text-xs text-gray-500 mt-2" data-testid="visit-modality-required">
          {config.reason || 'A video visit is required for this treatment in your state.'}
        </p>
      )}
    </div>
  );
};

export default VisitModalityChoice;
